"use client";

import { Download, Share, SquarePlus, X } from "lucide-react";
import { Suspense, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useSitePath } from "@/components/ui/use-site-path";
import { useUi } from "@/components/ui/use-ui";
import { cn } from "@/lib/cn";
import { rich } from "@/lib/locales/fmt";
import { dismissInstall, installDismissed, markInstalled, promptInstall, usePwa } from "@/lib/pwa";

// Sweet as an app on the home screen: the service worker (public/sw.js), the install banner on phones,
// the install card on the account page and the install row in the menu. On Android and in desktop Chrome
// the browser's own install dialog opens; on iPhone and iPad, the steps through the Share menu.

const ICON = "/brand/icon-192.png";
/** The banner waits a few seconds into a visit instead of greeting the visitor with it. */
const BANNER_DELAY_MS = 6000;
/** Pages where the banner would be in the way. */
const NO_BANNER = /^\/(shorts|offline|login|signup|forgot-password|reset-password)(\/|$)/;

/** Registers the service worker in production; in development a worker left from a production run would serve old code. */
export function ServiceWorker() {
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    if (process.env.NODE_ENV !== "production") {
      void navigator.serviceWorker.getRegistrations().then((regs) => regs.forEach((r) => r.unregister()));
      return;
    }
    const register = () => navigator.serviceWorker.register("/sw.js", { scope: "/", updateViaCache: "none" }).catch(() => {});
    if (document.readyState === "complete") register();
    else window.addEventListener("load", register, { once: true });
  }, []);
  return null;
}

const b = (text: string) => <b className="font-medium text-fg">{text}</b>;

/** The steps on iPhone and iPad, where Safari has no install dialog. "Got it" counts as installed: Safari can't
 * tell a page it was added, and the app on the Home Screen keeps its own storage. Put in <body>, out of the
 * menu's and cards' frosted boxes, which would hold a fixed sheet inside them. */
function IosSteps({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const t = useUi().pwa;
  useEffect(() => {
    const esc = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onClose]);
  const badge = "grid size-10 shrink-0 place-items-center rounded-full";
  const iconBadge = cn(badge, "border border-line bg-white/[0.06] text-fg");
  return createPortal(
    <div role="dialog" aria-modal="true" aria-labelledby="pwa-ios-title" onClick={onClose} className="fixed inset-0 z-[90] flex items-end justify-center bg-black/60 animate-[pwa-fade_.2s_ease-out]">
      <div
        onClick={(e) => e.stopPropagation()}
        className="glass-strong w-full max-w-md rounded-t-3xl px-5 pb-[calc(1.4rem+env(safe-area-inset-bottom))] pt-5 animate-[pwa-rise_.25s_ease-out]"
      >
        <div className="flex items-start gap-3">
          {/* eslint-disable-next-line @next/next/no-img-element -- the app's icon */}
          <img src={ICON} alt="" width={44} height={44} className="size-11 shrink-0 rounded-xl" />
          <h2 id="pwa-ios-title" className="flex-1 pt-0.5 text-[19px] leading-tight text-fg">
            {t.iosTitle}
          </h2>
          <button type="button" onClick={onClose} aria-label={t.close} className="-mr-2 -mt-1 grid size-11 shrink-0 place-items-center rounded-full text-muted hover:bg-white/10 hover:text-fg">
            <X size={22} />
          </button>
        </div>
        <ol className="mt-5 grid gap-3 text-[15px] leading-snug text-soft">
          <li className="flex items-center gap-3">
            <span className={iconBadge}>
              <Share size={20} strokeWidth={1.8} />
            </span>
            <span>{rich(t.iosShare, { share: b(t.share), more: b(t.more) })}</span>
          </li>
          <li className="flex items-center gap-3">
            <span className={iconBadge}>
              <SquarePlus size={20} strokeWidth={1.8} />
            </span>
            <span>{rich(t.iosAdd, { add: b(t.add) })}</span>
          </li>
          <li className="flex items-center gap-3">
            <span className={cn(badge, "bg-brand-strong font-medium text-white")}>3</span>
            <span>{rich(t.iosConfirm, { confirm: b(t.confirm) })}</span>
          </li>
        </ol>
        <button type="button" onClick={onDone} className="mt-6 h-12 w-full rounded-full bg-brand-strong text-[15px] font-medium text-white transition-colors hover:bg-brand-deep">
          {t.gotIt}
        </button>
      </div>
    </div>,
    document.body,
  );
}

/** Installing: the browser's dialog where there is one, the iPhone steps otherwise. */
export function useInstall() {
  const pwa = usePwa();
  const [showSteps, setShowSteps] = useState(false);
  const available = !pwa.standalone && (pwa.canPrompt || pwa.ios);
  const install = async () => {
    if (pwa.canPrompt) await promptInstall();
    else if (pwa.ios) setShowSteps(true);
  };
  const steps = showSteps ? (
    <IosSteps
      onClose={() => setShowSteps(false)}
      onDone={() => {
        markInstalled();
        setShowSteps(false);
      }}
    />
  ) : null;
  return { ...pwa, available, install, steps };
}

/**
 * On phones, above the bottom menu a few seconds into a visit. Closed, it stays away for two weeks; once
 * the visitor has the app, for good. Hidden (globals.css) until the age notice is accepted (`ageGate`)
 * and while the cookie choice is open.
 */
export function InstallBanner({ ageGate }: { ageGate: boolean }) {
  // It reads the address, which only the request knows.
  return (
    <Suspense fallback={null}>
      <Banner ageGate={ageGate} />
    </Suspense>
  );
}

function Banner({ ageGate }: { ageGate: boolean }) {
  const t = useUi().pwa;
  const path = useSitePath();
  const { available, installed, install, steps } = useInstall();
  const [ready, setReady] = useState(false);
  const [closed, setClosed] = useState(false);

  useEffect(() => {
    if (installDismissed()) return;
    const id = window.setTimeout(() => setReady(true), BANNER_DELAY_MS);
    return () => window.clearTimeout(id);
  }, []);

  if (steps) return steps;
  if (!ready || closed || installed || !available || NO_BANNER.test(path)) return null;

  const close = () => {
    dismissInstall();
    setClosed(true);
  };

  return (
    <div
      role="region"
      aria-label={t.bannerLabel}
      data-install-banner
      data-after-age={ageGate || undefined}
      className="fixed inset-x-2 bottom-[calc(max(0.5rem,env(safe-area-inset-bottom))+4rem)] z-30 animate-[pwa-rise_.25s_ease-out] md:hidden"
    >
      <div className="glass-strong flex items-center gap-3 rounded-2xl py-2.5 pl-2.5 pr-1.5 shadow-[0_20px_60px_-15px_rgb(0_0_0/0.9)]">
        {/* eslint-disable-next-line @next/next/no-img-element -- the app's icon */}
        <img src={ICON} alt="" width={44} height={44} className="size-11 shrink-0 rounded-xl" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-[15px] font-medium leading-tight text-fg">{t.appName}</p>
          <p className="mt-0.5 text-[12.5px] leading-snug text-muted">{t.bannerPitch}</p>
        </div>
        <button type="button" onClick={install} className="h-10 shrink-0 rounded-full bg-brand-strong px-4 text-[14px] font-medium text-white transition-colors hover:bg-brand-deep">
          {t.install}
        </button>
        <button type="button" onClick={close} aria-label={t.notNow} title={t.notNow} className="grid h-10 w-9 shrink-0 place-items-center rounded-full text-muted hover:bg-white/10 hover:text-fg">
          <X size={20} />
        </button>
      </div>
    </div>
  );
}

/** The account page's card: install, or that this is the app already. */
export function InstallCard() {
  const t = useUi().pwa;
  const { available, standalone, install, steps, canPrompt } = useInstall();
  return (
    <section aria-labelledby="pwa-card-title" className="glass rounded-2xl p-5 md:p-6">
      <div className="flex items-center gap-3.5">
        {/* eslint-disable-next-line @next/next/no-img-element -- the app's icon */}
        <img src={ICON} alt="" width={48} height={48} className="size-12 shrink-0 rounded-xl" />
        <div className="min-w-0 flex-1">
          <h2 id="pwa-card-title" className="text-[16px] text-fg">
            {t.appName}
          </h2>
          <p className="mt-0.5 text-[13.5px] leading-snug text-muted">{standalone ? t.inApp : t.cardPitch}</p>
        </div>
      </div>
      {available ? (
        <button
          type="button"
          onClick={install}
          className="mt-4 flex h-11 w-full items-center justify-center gap-2 rounded-full bg-brand-strong text-[14.5px] font-medium text-white transition-colors hover:bg-brand-deep"
        >
          <Download size={18} /> {canPrompt ? t.installApp : t.howToInstall}
        </button>
      ) : (
        !standalone && <p className="mt-3 text-[13.5px] leading-relaxed text-muted">{rich(t.manual, { install: b(t.installMenu), add: b(t.add) })}</p>
      )}
      {steps}
    </section>
  );
}

/** The menu's install row, while installing is on offer. */
export function InstallRow({ className, label }: { className: string; label: (text: string) => React.ReactNode }) {
  const t = useUi().pwa;
  const { available, installed, install, steps } = useInstall();
  if (steps) return steps;
  if (!available || installed) return null;
  return (
    <button type="button" onClick={install} title={t.installApp} className={className}>
      <Download size={22} strokeWidth={1.6} className="shrink-0" />
      {label(t.installApp)}
    </button>
  );
}
